import React, { useCallback, useEffect, useLayoutEffect } from 'react';
import { StyleSheet } from 'react-native';
import Animated from 'react-native-reanimated';
import { AppView, CardView, rem, SCREEN_BOTTOM_PADDING, withAnimatedNavigationHeader, } from 'design-system';
import ListEmptyView from 'design-system/ListEmptyView';
import SectionHeader from 'design-system/SectionHeader';
import UpgradeButton from 'design-system/UpgradeButton';
import { UpgradeWebModal } from 'design-system/UpgradeWebModal';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useAppLink from 'hooks/useAppLink';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { benefits, tierThemes } from 'features/premium/benefits';
import { Tier } from 'features/premium/entities';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/hooks';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { isWeb } from '../../../constants';
import { getCreditAlerts, markCreditAlertsAsRead } from '../actions';
import CreditAlertsNotificationCard from '../components/CreditAlertsNotificationCard';
import CreditScoreAlertItem from '../components/CreditScoreAlertItem';
import { selectCreditAlertsSections, selectCreditAlertsUnreadCount, selectFullCreditAlertsUnlocked, } from '../selectors';
const keyExtractor = (item) => 'sectionTitle' in item ? `section-${item.id}` : item.alertId;
const strings = {
    upgradeTitle: 'Unlock credit alerts',
};
const CreditScoreAlertsScreen = withAnimatedNavigationHeader(({ scrollHandler, navigation }) => {
    const dispatch = useAppDispatch();
    const store = useAppStore();
    const { appLinkTo } = useAppLink();
    const entries = useAppSelector(selectCreditAlertsSections);
    const fullAlertsUnlocked = useAppSelector(selectFullCreditAlertsUnlocked);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', SCREEN_BOTTOM_PADDING, [styles.contentContainer, paddingHorizontalStyle]);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton />,
        });
    }, [navigation]);
    useEffect(() => {
        dispatch(getCreditAlerts());
    }, [dispatch]);
    useEffect(() => {
        const unsubscribe = navigation.addListener('beforeRemove', () => {
            if (selectCreditAlertsUnreadCount(store.getState()) > 0) {
                dispatch(markCreditAlertsAsRead([]));
            }
        });
        return unsubscribe;
    }, [navigation, dispatch, store]);
    const renderItem = useCallback(({ item }) => {
        if ('sectionTitle' in item) {
            return (<SectionHeader horizontalMargins style={styles.sectionHeader}>
                {item.sectionTitle}
              </SectionHeader>);
        }
        return (<CreditScoreAlertItem item={item} style={styles.alertRow} shouldShake={!fullAlertsUnlocked} onPress={() => {
                if (!item.isRead) {
                    dispatch(markCreditAlertsAsRead([item.alertId]));
                }
                appLinkTo(item.appLink);
            }}/>);
    }, [appLinkTo, dispatch, fullAlertsUnlocked]);
    const ListEmpty = useCallback(() => <ListEmptyView happy onCard title="You have no credit alerts"/>, []);
    const onPressUpgrade = useCallback(() => {
        const { minTier } = benefits.creditScoreAlerts;
        if (minTier === Tier.free)
            return;
        if (isWeb) {
            UpgradeWebModal.show(minTier);
            return;
        }
        navigation.navigate('EmmaProModal', {
            tier: minTier,
            benefitId: benefits.creditScoreAlerts.id,
        });
    }, [navigation]);
    const ListFooter = useCallback(() => {
        if (fullAlertsUnlocked)
            return null;
        const { minTier } = benefits.creditScoreAlerts;
        const tierTheme = tierThemes[minTier];
        const upgradeText = `Upgrade to ${tierTheme.name} to receive daily credit alerts. It’s the easiest way to monitor your progress and boost your score!`;
        return (<CardView style={styles.upgradeCard} noMarginTop>
            <UpgradeButton onCard chipText={strings.upgradeTitle} text={upgradeText} onPress={onPressUpgrade}/>
          </CardView>);
    }, [fullAlertsUnlocked, onPressUpgrade]);
    return (<AppView withFrame={false}>
          <Animated.FlatList data={entries} onScroll={scrollHandler} scrollEventThrottle={16} keyExtractor={keyExtractor} renderItem={renderItem} ListHeaderComponent={CreditAlertsNotificationCard} ListEmptyComponent={fullAlertsUnlocked ? ListEmpty : null} ListFooterComponent={ListFooter} contentContainerStyle={contentContainerStyle} showsVerticalScrollIndicator/>
        </AppView>);
}, () => ({
    title: 'Credit alerts',
}));
const styles = StyleSheet.create({
    contentContainer: {
        paddingTop: rem(16),
    },
    sectionHeader: {
        marginBottom: rem(16),
    },
    alertRow: {
        marginBottom: rem(16),
        marginHorizontal: rem(16),
    },
    upgradeCard: {
        marginHorizontal: rem(16),
        paddingTop: rem(32),
        paddingBottom: rem(40),
        paddingHorizontal: rem(16),
    },
});
export default CreditScoreAlertsScreen;
